import BookmarkAddOutlinedIcon from '@mui/icons-material/BookmarkAddOutlined'
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined'
import {useEffect, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {
  StyledStickyFooterBox,
  StyledSaveFiltersPaper,
  StyledSaveFiltersStack,
  StyledClearAllButton,
  StyledSaveFiltersOutlinedButton,
  StyledAppliedFilterChip,
  StyledAppliedSaveFiltersPaper
} from '../FilterPanelStyle'
import SaveFiltersDialog from './SaveFiltersDialog'

import ClearIcon from '@mui/icons-material/Clear'
import {Typography} from '@mui/material'
import {useSavedFilter} from '../../../store/hooks/savedFilter'
import useFilterManagement from '../../../store/hooks/useFilterManagement'

const SaveFiltersBar = ({clearAllFilters}) => {
  const [open, setOpen] = useState(false) // Changed to false
  const [disabled, setDisabled] = useState(false)
  const [duplicateFilter, setDuplicateFilter] = useState('')

  // Redux store hooks for saved filters
  const {
    appliedSavedFilterName,
    setCurrentAppliedSavedFilterName,
    savedFilterList,
    setCurrentFilterCombinationName,
    changesFromSavedFilter,
    setFilterChangesFromSavedFilter
  } = useSavedFilter()

  // Redux store hooks for filters management
  const {addedFilters} = useFilterManagement()

  // Handler to toggle dialog open state
  const toggleOpen = () => {
    setOpen((prev) => !prev)
  }

  // ✅ Track previous addedFilters to detect changes
  const prevAddedFiltersRef = useRef(addedFilters)

  // ✅ Clear appliedSavedFilterName when filters change
  useEffect(() => {
    const prevFilters = prevAddedFiltersRef.current

    // Check if filters have actually changed (not just reference)
    const filtersChanged =
      prevFilters.length !== addedFilters.length ||
      !prevFilters.every((filter, index) => filter === addedFilters[index])

    if (changesFromSavedFilter) {
      setFilterChangesFromSavedFilter(false)
    }
    // If filters changed and the change is not from applying a saved filter, clear the appliedSavedFilterName
    else if (filtersChanged && appliedSavedFilterName) {
      setCurrentAppliedSavedFilterName('')
    }

    // Update ref for next comparison
    prevAddedFiltersRef.current = addedFilters
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addedFilters, appliedSavedFilterName, setCurrentAppliedSavedFilterName, changesFromSavedFilter])

  // check for duplicate filters combination which already exists in saved filters
  useEffect(() => {
    if (addedFilters.length === 0 || appliedSavedFilterName !== '' || savedFilterList.length === 0) {
      setDuplicateFilter('')
      return
    }

    //  ✅ Find matching saved filter
    const matchingFilter = savedFilterList.find((filter) => {
      // Check if lengths match first
      if (addedFilters.length !== filter.filters.length) {
        return false
      }
      // Extract filter keys from saved filter
      const savedFilterKeys = filter.filters.map((f) => f.filterKey)

      // ✅ Sort both arrays before comparing
      const sortedAddedFilters = [...addedFilters].sort((a, b) => a.localeCompare(b))
      const sortedSavedFilters = [...savedFilterKeys].sort((a, b) => a.localeCompare(b))

      // Compare sorted arrays
      const isSame = sortedAddedFilters.every((val, index) => val === sortedSavedFilters[index])

      return isSame
    })

    if (matchingFilter) {
      setDuplicateFilter(matchingFilter.name) // Componnent level state to show duplicate message
      setCurrentFilterCombinationName(matchingFilter.name) // Redux store state to disable save button
    } else {
      setDuplicateFilter('')
      setCurrentFilterCombinationName('')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedFilterList, addedFilters, appliedSavedFilterName])

  // Auto-update disabled state when appliedSavedFilterName changes
  // ✅ Auto-update disabled state
  useEffect(() => {
    // Disable if:
    // 1. No filters added
    // 2. A saved filter is already applied
    // 3. Current filters are a duplicate of existing saved filter
    const shouldDisable = addedFilters.length === 0 || !!appliedSavedFilterName || !!duplicateFilter

    setDisabled(shouldDisable)
  }, [appliedSavedFilterName, duplicateFilter, addedFilters.length])

  // ✅ Simplified handler
  const handleClearAllFilters = () => {
    if (appliedSavedFilterName) {
      setCurrentAppliedSavedFilterName('')
    }
    clearAllFilters()
  }

  return (
    <>
      <StyledStickyFooterBox position='sticky' bottom={0} mt='auto'>
        {/* Applied Saved filter banner here */}
        {appliedSavedFilterName && (
          <StyledAppliedSaveFiltersPaper sx={{p: 1}} elevation={16}>
            <Typography variant='body1'>
              <i> Applied saved filters:</i>{' '}
              <StyledAppliedFilterChip
                label={appliedSavedFilterName}
                onDelete={() => {
                  setCurrentAppliedSavedFilterName('')
                }}
                deleteIcon={<ClearIcon />}
              />
            </Typography>
          </StyledAppliedSaveFiltersPaper>
        )}

        {duplicateFilter && (
          <StyledAppliedSaveFiltersPaper elevation={16}>
            <Typography variant='subtitle1'>
              Note : This filter combination is already saved as &nbsp; <br />
              &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; <strong>{duplicateFilter}</strong>
            </Typography>
          </StyledAppliedSaveFiltersPaper>
        )}

        {/* Bottom Save Filters Bar */}
        <StyledSaveFiltersPaper elevation={16}>
          <StyledSaveFiltersStack>
            <StyledClearAllButton startIcon={<ReplayOutlinedIcon />} color='error' onClick={handleClearAllFilters}>
              Clear All
            </StyledClearAllButton>

            <StyledSaveFiltersOutlinedButton
              variant='outlined'
              startIcon={<BookmarkAddOutlinedIcon />}
              onClick={toggleOpen}
              disabled={disabled}
            >
              Save Selected Filters
            </StyledSaveFiltersOutlinedButton>
          </StyledSaveFiltersStack>
        </StyledSaveFiltersPaper>
      </StyledStickyFooterBox>

      <SaveFiltersDialog
        open={open}
        onClose={toggleOpen}
        currentFilterName=''
        data={{title: 'Save selected filters', icon: false}}
      />
    </>
  )
}

export default SaveFiltersBar

SaveFiltersBar.propTypes = {
  clearAllFilters: PropTypes.func.isRequired
}
